import React, { useEffect, useState } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import Box from "@mui/material/Box";
import Button, { ButtonProps } from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Typography, { TypographyProps } from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { SxProps } from "@mui/system";

interface SubSection {
    id: string;
    header?: string;
    subtitle?: string;
    content?: JSX.Element | JSX.Element[];
    action?: JSX.Element | JSX.Element[];

}

interface ReportSectionProps {
    id: string;
    header: string;
    subtitle?: string;
    headerProps?: TypographyProps;
    headerAction?: React.ReactNode;
    headerRight?: React.ReactNode;
    topDivider?: boolean;
    subsections: SubSection[];
    sx?: SxProps;
    expanded?: boolean;
    setExpanded?: (val: boolean) => void;
    showExpandToggle?: boolean;
}

export const ReportSection = ({
    id,
    header,
    subtitle,
    headerProps = {},
    headerAction,
    headerRight,
    topDivider = false,
    subsections,
    sx,
    expanded: propExpanded,
    setExpanded: propSetExpanded,
    showExpandToggle = true,
}: ReportSectionProps) => {
    const [expanded, setExpanded] = useState(propExpanded ?? true);

    useEffect(() => {
        if (propExpanded !== void 0 && propExpanded !== expanded) {
            setExpanded(propExpanded);
        }
    }, [propExpanded]);

    const handleChange = () => {
        if (!!propSetExpanded) {
            propSetExpanded(!expanded);
        } else {
            setExpanded(!expanded);
        }
    };

    return (
        <Accordion
            variant="elevation"
            expanded={expanded}
            onChange={handleChange}
            sx={{ width: "100%", ...sx }}
        >
            <Box display="flex" justifyContent="space-between" padding={2}>
                <Box>
                    <Typography
                        marginBottom={0}
                        variant="h3"
                        display="inline-block"
                        alignSelf="center"
                        sx={{ verticalAlign: "middle" }}
                        {...headerProps}
                    >
                        {header}
                    </Typography>
                    {!!subtitle ? (
                        <Typography
                            ml={2}
                            variant="h4"
                            color="green.dark"
                            display="inline-block"
                            sx={{ verticalAlign: "middle", lineHeight: 2 }}
                        >
                            {subtitle}
                        </Typography>
                    ) : null}
                    {headerAction}
                </Box>
                {headerRight}
                {showExpandToggle && expanded ? (
                    <ExpandLessButton
                        id={`report-section-${id}-expand-less`}
                        onClick={handleChange}
                    />
                ) : showExpandToggle ? (
                    <ExpandMoreButton
                        id={`report-section-${id}-expand-more`}
                        onClick={handleChange}
                    />
                ) : null}
            </Box>
            <AccordionDetails>
                {topDivider ? <Divider sx={{ mb: 3 }} /> : null}
                {subsections.map((s, i) => (
                    <React.Fragment key={s.id}>
                        {!!s.header || !!s.action ? (
                            <Box display="flex" justifyContent="space-between">
                                <Box>
                                    <Typography
                                        variant="h3"
                                        display="inline-block"
                                    >
                                        {s.header}
                                    </Typography>
                                    {!!s.subtitle ? (
                                        <Typography
                                            variant="body2"
                                            ml={3}
                                            display="inline-block"
                                            color="grey"
                                        >
                                            {s.subtitle}
                                        </Typography>
                                    ) : null}
                                </Box>
                                {s.action}
                            </Box>
                        ) : null}
                        {s.content ?? null}
                        {i < subsections.length - 1 ? (
                            <Divider sx={{ my: 3 }} />
                        ) : null}
                    </React.Fragment>
                ))}
            </AccordionDetails>
        </Accordion>
    );
};

interface ReportButtonProps {
    color?: ButtonProps["color"];
    onClick: () => void;
    children: string | JSX.Element;
    variant?: ButtonProps["variant"];
    disabled?: boolean;
}

export const ReportButton = ({
    color = "primary",
    onClick,
    children,
    variant = "contained",
    disabled,
}: ReportButtonProps) => {
    return (
        <Button
            color={color}
            sx={{
                height: 40,
            }}
            onClick={onClick}
            variant={variant}
            disabled={disabled}
        >
            {children}
        </Button>
    );
};

export const ExpandLessButton = (props: ButtonProps) => {
    const { t } = useTranslation();

    return (
        <Button startIcon={<ExpandLessIcon />} {...props}>
            {t("reporting.hideSelection")}
        </Button>
    );
};

export const ExpandMoreButton = (props: ButtonProps) => {
    const { t } = useTranslation();

    return (
        <Button startIcon={<ExpandMoreIcon />} {...props}>
            {t("reporting.showSelection")}
        </Button>
    );
};